/**
 * Formatting helpers shared across page components.
 *
 * Locale-aware as of the i18n scaffold commit — `formatMoney` uses
 * `Intl.NumberFormat` with the current i18next locale (browser-detected,
 * `en` fallback) and a configurable currency that defaults to `CAD`.
 *
 * `formatPhone` remains locale-naive — North-American phone formatting
 * is the same in every locale we ship for now.
 */

import i18n from '@/lib/i18n'

const DEFAULT_CURRENCY = 'CAD'

/**
 * Format a North-American phone number for display.
 *
 * Accepts E.164 (`+14165551234`), digits-only (`14165551234`,
 * `4165551234`), or anything pre-formatted. Returns the original input
 * if it doesn't look like a 10/11-digit NANP number, so user-entered
 * exotic formats are preserved instead of corrupted.
 *
 * Returns the em-dash `—` for null/empty input.
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '—'
  const digits = phone.replace(/\D/g, '')
  const normalized =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (normalized.length !== 10) return phone
  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`
}

/**
 * Format a currency amount with locale-aware grouping + symbol.
 *
 * - Locale defaults to the current i18next language (which respects
 *   the user's browser locale, e.g. `en-CA` → "CA$1,234.56",
 *   `en-US` → "CAD 1,234.56" depending on ICU).
 * - Currency defaults to `CAD`. Pass an override per-call when the
 *   value comes from a row that carries its own currency code (an
 *   estimate row's `currency` column, etc.).
 * - Returns `''` for null/undefined so callers can ergonomically
 *   render empty cells without an `if` ladder. Pass `0` explicitly to
 *   render a real zero-amount string.
 */
/**
 * Manufacturer names that get shortened on tight surfaces (appointment
 * tiles, day-list rows) so the model name survives truncation. Lookup
 * key is the uppercased input; the value is the display short form.
 *
 * Conservative list — only manufacturers whose canonical name is long
 * enough that the full year+make+model wouldn't fit on a narrow tile.
 * VINs and DB rows keep the full make verbatim; this is display-only.
 */
const MAKE_SHORT_FORMS: Record<string, string> = {
  'MERCEDES-BENZ': 'MERCEDES',
  MERCEDESBENZ: 'MERCEDES',
  VOLKSWAGEN: 'VW',
  'ROLLS-ROYCE': 'ROLLS',
  ROLLSROYCE: 'ROLLS',
  CHEVROLET: 'CHEVY',
}

/**
 * Format a vehicle's year + make + model into a single display line.
 *
 * Make and model are uppercased ("LEXUS RX 350") to match the
 * existing appointment-tile visual treatment; the year prefixes when
 * present, so the full string reads "2024 LEXUS RX 350". Long
 * manufacturer names get shortened to their commonly-used short form
 * (MERCEDES-BENZ → MERCEDES, VOLKSWAGEN → VW) so the model survives
 * truncation on a narrow tile. With only one of make/model the other
 * side is dropped; year never renders alone (a bare "2024" tile is
 * meaningless to the mechanic).
 *
 * Returns an empty string when both make and model are null — callers
 * decide what fallback to render ("No vehicle linked", `—`, etc.).
 */
export function formatVehicleLine(
  year: number | null | undefined,
  make: string | null | undefined,
  model: string | null | undefined,
): string {
  const rawMake = make?.trim().toUpperCase() ?? ''
  const m = MAKE_SHORT_FORMS[rawMake] ?? rawMake
  const md = model?.trim().toUpperCase() ?? ''
  const base = [m, md].filter(Boolean).join(' ')
  if (!base) return ''
  return year ? `${year} ${base}` : base
}

/**
 * Format an integer with locale-aware digit grouping (e.g. `124300` →
 * "124,300" in `en`, "124 300" in `fr`). Locale defaults to the current
 * i18next language, mirroring `formatMoney`. Use for counts like odometer
 * readings — pass the raw number, not a pre-`.toLocaleString()`'d string.
 */
export function formatInteger(
  value: number,
  opts: { locale?: string } = {},
): string {
  const locale = opts.locale ?? i18n.language ?? 'en'
  try {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
      value,
    )
  } catch {
    return String(Math.round(value))
  }
}

export function formatMoney(
  amount: number | null | undefined,
  opts: { currency?: string; locale?: string } = {},
): string {
  if (amount === null || amount === undefined) return ''
  const currency = opts.currency ?? DEFAULT_CURRENCY
  const locale = opts.locale ?? i18n.language ?? 'en'
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(amount)
  } catch {
    // Defensive fallback — Intl can throw on bad currency codes (e.g.
    // a typo in DB data). Render the number with a neutral prefix
    // rather than crash the whole row.
    return `${currency} ${amount.toFixed(2)}`
  }
}
